import React from "react";
import { Link, NavLink } from "react-router-dom";
import { useLang, LANGUAGES } from "../lib/lang";
import { useAuth } from "../lib/auth";
import { Shield, Menu, X } from "lucide-react";
import { useState } from "react";

export default function Header() {
  const { lang, setLang, t } = useLang();
  const { user } = useAuth();
  const [open, setOpen] = useState(false);

  const navItems = [
    { to: "/", label: t("nav_home"), id: "home" },
    { to: "/news", label: t("nav_news"), id: "news" },
    { to: "/gallery", label: t("nav_gallery"), id: "gallery" },
    { to: "/events", label: t("nav_events"), id: "events" },
    { to: "/about", label: t("nav_about"), id: "about" },
    { to: "/contact", label: t("nav_contact"), id: "contact" },
  ];

  return (
    <header className="sticky top-0 z-50 bg-white/95 backdrop-blur-md border-b border-slate-200" data-testid="site-header">
      <div className="kn-divider" />
      <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-3 group" data-testid="brand-link">
          <div className="w-11 h-11 bg-slate-900 text-white flex items-center justify-center" aria-hidden>
            <Shield className="w-6 h-6" strokeWidth={1.5} />
          </div>
          <div className="leading-tight">
            <div className="font-display text-xl tracking-wider text-slate-900" data-testid="brand-title">
              {t("brand")}
            </div>
            <div className="text-[10px] uppercase tracking-[0.25em] text-slate-500">
              {t("tagline")}
            </div>
          </div>
        </Link>

        <nav className="hidden lg:flex items-center gap-8" data-testid="primary-nav">
          {navItems.map((n) => (
            <NavLink
              key={n.id}
              to={n.to}
              end={n.to === "/"}
              data-testid={`nav-${n.id}`}
              className={({ isActive }) =>
                `text-sm uppercase tracking-[0.18em] font-medium transition-colors ${
                  isActive ? "text-red-600" : "text-slate-700 hover:text-slate-900"
                }`
              }
            >
              {n.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <div className="hidden md:flex items-center border border-slate-300" data-testid="lang-switcher">
            {LANGUAGES.map((l) => (
              <button
                key={l.code}
                onClick={() => setLang(l.code)}
                data-testid={`lang-${l.code}`}
                className={`px-3 py-1.5 text-xs font-semibold uppercase tracking-wider transition ${
                  lang === l.code ? "bg-slate-900 text-white" : "bg-white text-slate-700 hover:bg-slate-50"
                }`}
              >
                {l.label}
              </button>
            ))}
          </div>
          {user && user.role === "admin" ? (
            <Link
              to="/admin"
              data-testid="nav-admin"
              className="hidden md:inline-block bg-red-600 text-white px-4 py-2 text-xs uppercase tracking-[0.18em] font-semibold hover:bg-red-700 transition"
            >
              {t("nav_admin")}
            </Link>
          ) : (
            <Link
              to="/login"
              data-testid="nav-signin"
              className="hidden md:inline-block border border-slate-900 px-4 py-2 text-xs uppercase tracking-[0.18em] font-semibold hover:bg-slate-900 hover:text-white transition"
            >
              {t("signin")}
            </Link>
          )}
          <button
            onClick={() => setOpen(!open)}
            className="lg:hidden w-10 h-10 flex items-center justify-center border border-slate-300"
            data-testid="mobile-menu-button"
            aria-label="menu"
          >
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden border-t border-slate-200 bg-white" data-testid="mobile-nav">
          <div className="max-w-7xl mx-auto px-6 py-4 flex flex-col gap-3">
            {navItems.map((n) => (
              <NavLink
                key={n.id}
                to={n.to}
                end={n.to === "/"}
                onClick={() => setOpen(false)}
                data-testid={`mobile-nav-${n.id}`}
                className="text-sm uppercase tracking-[0.18em] font-medium py-2 border-b border-slate-100"
              >
                {n.label}
              </NavLink>
            ))}
            <div className="flex gap-2 pt-2">
              {LANGUAGES.map((l) => (
                <button
                  key={l.code}
                  onClick={() => setLang(l.code)}
                  className={`flex-1 px-3 py-2 text-xs font-semibold border ${
                    lang === l.code ? "bg-slate-900 text-white border-slate-900" : "border-slate-300"
                  }`}
                  data-testid={`mobile-lang-${l.code}`}
                >
                  {l.label}
                </button>
              ))}
            </div>
            {user && user.role === "admin" ? (
              <Link to="/admin" className="bg-red-600 text-white py-3 text-center text-xs uppercase tracking-widest" data-testid="mobile-nav-admin">
                {t("nav_admin")}
              </Link>
            ) : (
              <Link to="/login" className="border border-slate-900 py-3 text-center text-xs uppercase tracking-widest" data-testid="mobile-nav-signin">
                {t("signin")}
              </Link>
            )}
          </div>
        </div>
      )}
    </header>
  );
}
